import { useState, useCallback, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, Download, FileDown, FileSpreadsheet } from 'lucide-react';
import apiClient from '@/api/client';

// ─── Export-Menü ────────────────────────────────────────────────────────────
//
// Dropdown-Menü mit zwei Optionen:
// - GEDCOM exportieren → GET /api/export/gedcom
// - CSV exportieren    → GET /api/export/csv
//
// Beide liefern den AKTIVEN Stammbaum. Der frühere Eintrag „Backup erstellen"
// ist hier verschwunden: er zog ein Abbild der ganzen Installation – aller
// Familien – und gehört damit in die Betreiberkonsole, nicht in ein Menü
// neben den Exporten des eigenen Baums.

export interface ExportMenuItem {
  label: string;
  description: string;
  path: string;
  filename: string;
  icon: React.ReactNode;
}

export const EXPORT_ITEMS: ExportMenuItem[] = [
  {
    label: 'GEDCOM exportieren',
    description: 'GEDCOM 5.5.1 (.ged)',
    path: '/export/gedcom',
    filename: 'stammbaum.ged',
    icon: <FileDown className="h-4 w-4" aria-hidden="true" />,
  },
  {
    label: 'CSV exportieren',
    description: 'Tabelle (.csv)',
    path: '/export/csv',
    filename: 'stammbaum.csv',
    icon: <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />,
  },
];

/** Datei via Axios herunterladen (mit Auth-Header) und als Blob-Download auslösen */
export async function downloadExportFile(path: string, fallbackFilename: string) {
  const response = await apiClient.get(path, { responseType: 'blob' });

  // Dateiname aus Content-Disposition Header extrahieren, falls vorhanden
  const disposition = response.headers['content-disposition'];
  let filename = fallbackFilename;
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) filename = match[1];
  }

  // Blob-URL erstellen und Download auslösen
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Klick außerhalb → Menü schließen
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ESC → schließen
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleDownload = useCallback(async (item: ExportMenuItem) => {
    setDownloading(item.path);
    try {
      await downloadExportFile(item.path, item.filename);
      toast.success(`${item.label} abgeschlossen.`);
    } catch {
      toast.error(`${item.label} fehlgeschlagen.`);
    } finally {
      setDownloading(null);
      setIsOpen(false);
    }
  }, []);

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger-Button */}
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Export</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown-Menü */}
      {isOpen && (
        <div className="absolute right-0 z-40 mt-1 w-56 origin-top-right rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {EXPORT_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => handleDownload(item)}
              disabled={downloading !== null}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <span className="shrink-0 text-slate-400">
                {downloading === item.path ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                ) : (
                  item.icon
                )}
              </span>
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-xs text-slate-400">{item.description}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
